// Automatic dream detail extraction — the browser's client for POST /api/dream/details.
//
// The endpoint was built for the phone and has been finished, tested and premium-gated for a while;
// the browser simply never called it. iOS fires it silently after a dream is saved and merges the
// result into the encrypted payload, so the Symbols / Details / Lucidity pages are already filled the
// next time the entry is opened. This does the same, and adds a visible control so the extraction can
// also be asked for on purpose.
//
// The merge fills gaps only, and the silent pass after save runs only while the form is still
// blank. Overwriting is the one thing this must never do: a dreamer who typed "my sister" and got
// "a woman" back would learn not to type anything, and a dreamer who deleted a tag and had it
// reappear on the next save would learn the same. The point of the feature is to save them work,
// not to correct them.

import { apiPost } from './client';
import { emptyDreamDetails, hasLoggedDreamDetails, type DreamDetails, type DreamPerspective, type DreamTone } from './dream-details';

/**
 * What the Worker returns. Its scalars are nullable where the payload's are not: null means "the
 * story gave no signal", which is different from the default the composer starts at, and collapsing
 * the two would have every dream reported as neutral and first-person.
 */
export interface ExtractedDetails {
  setting: string[];
  atmosphere: string[];
  emotions: string[];
  elements: string[];
  characters: string[];
  themes: string[];
  actions: string[];
  lucidity: number | null;
  vividness: number | null;
  tone: DreamTone | null;
  perspective: DreamPerspective | null;
  recurring: boolean | null;
}

/**
 * Below this the story is not yet a dream worth reading. iOS's own floor — a fragment produces
 * confident nonsense, and spending a model call plus a slice of the daily AI budget on it is worse
 * than doing nothing.
 */
export const MIN_STORY_CHARS = 40;

/** iOS caps each list at 8 on merge, so a long dream cannot bury what the dreamer typed. */
const LIST_CAP = 8;

export function longEnoughToExtract(story: string): boolean {
  return story.trim().length >= MIN_STORY_CHARS;
}

/**
 * Ask the Worker to read a dream and name its parts.
 *
 * Throws on a gate or a network failure so a deliberate press can say why nothing happened; the
 * automatic path swallows it, since a background extraction that quietly did not run is not
 * something to interrupt someone's writing about.
 */
export async function extractDreamDetails(
  story: string,
  signal?: AbortSignal,
): Promise<ExtractedDetails | null> {
  const trimmed = story.trim();
  if (!longEnoughToExtract(trimmed)) return null;

  const res = await apiPost<{ details?: ExtractedDetails | null }>(
    '/api/dream/details',
    { story: trimmed },
    signal,
  );
  return res.details ?? null;
}

const DEFAULTS = emptyDreamDetails();

function appendMissing(existing: string[], incoming: string[]): string[] {
  const seen = new Set(existing.map((v) => v.trim().toLowerCase()));
  const out = [...existing];

  for (const raw of incoming) {
    const value = raw.trim();
    if (!value || out.length >= LIST_CAP) continue;
    const key = value.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(value);
  }

  return out;
}

/**
 * Fold an extraction into the payload, filling gaps only. Mirrors iOS's `DreamDetailsService.merge`.
 *
 * Lists gain the values that are not already there. Scalars are set only while they are still at the
 * default the composer starts at — a dreamer who moved the lucidity slider has said something, and a
 * model reading the prose does not get to disagree with them.
 *
 * Returns the same object when nothing changed, so a caller can skip a re-render and, more
 * importantly, skip a save.
 */
export function mergeExtracted(current: DreamDetails, extracted: ExtractedDetails): DreamDetails {
  const next: DreamDetails = {
    ...current,
    setting: appendMissing(current.setting, extracted.setting),
    atmosphere: appendMissing(current.atmosphere, extracted.atmosphere),
    emotions: appendMissing(current.emotions, extracted.emotions),
    elements: appendMissing(current.elements, extracted.elements),
    characters: appendMissing(current.characters, extracted.characters),
    themes: appendMissing(current.themes, extracted.themes),
    actions: appendMissing(current.actions, extracted.actions),
  };

  const { lucidity, vividness, tone, perspective, recurring } = extracted;
  if (lucidity != null && lucidity >= 1 && lucidity <= 5 && current.lucidity === DEFAULTS.lucidity) {
    next.lucidity = lucidity;
  }
  if (vividness != null && vividness >= 1 && vividness <= 5 && current.vividness === DEFAULTS.vividness) {
    next.vividness = vividness;
  }
  if (tone && current.tone === DEFAULTS.tone) next.tone = tone;
  if (perspective && current.perspective === DEFAULTS.perspective) next.perspective = perspective;
  if (recurring === true && !current.recurring) next.recurring = true;

  return changed(current, next) ? next : current;
}

/** Stamp that Oreia has had its pass, so a later save cannot silently extract again. */
export function markOreiaFilled(details: DreamDetails): DreamDetails {
  return details.oreiaFilled ? details : { ...details, oreiaFilled: true };
}

/**
 * Whether a save may run the silent extraction iOS fires after a dream is first written.
 *
 * Once only, and only into a blank form. A dreamer who typed (or deleted) tags has said something,
 * and appending what the model thinks is missing is how those tags used to change behind their back.
 */
export function shouldSilentBackfill(details: DreamDetails): boolean {
  return !details.oreiaFilled && !hasLoggedDreamDetails(details);
}

function changed(before: DreamDetails, after: DreamDetails): boolean {
  return JSON.stringify(before) !== JSON.stringify(after);
}
